import React from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { attachmentKindFromContentType, getAttachmentDownloadUrl, getMessageTypeLabel, inferContentTypeFromFilename } from "api/chat";
import MediaViewer from "components/common/MediaViewer";
import { downloadMedia, describeNativeSaveResult, NATIVE_SAVE_PENDING_MESSAGE, NATIVE_SAVE_UNKNOWN_MESSAGE } from "utils/mediaDownload";
import { isDevEnv } from "utils/index";
import { useSnackbar } from "contexts/SnackbarContext";
import alertIcon from "assets/images/icons/alert.svg";

// dev-chat-photo-only-0915: 채팅 첨부 사진-only 정책 플래그.
// true면 첨부 tray 에 사진 액션만 두고 file input 경계(addFiles)에서 image/* 외 파일을 거부하며,
// 기존 메시지의 일반파일 다운로드·영상 저장 컨트롤을 렌더하지 않는다(보기·재생은 유지).
// dev-chat-attachments-all-kinds-1014: dev 에서도 사진·동영상·파일 3종을 모두 열기 위해 기본값을 false 로 내렸다.
// 운영 차단은 이 플래그가 아니라 아래 MESSAGE_ATTACHMENT_COMING_SOON(!isDevEnv)이 담당하므로 운영 동작은 바뀌지 않는다.
// 사진-only 로 되돌리려면 Boolean(isDevEnv) 로 복구하면 된다(prop `photoOnly` 로 호출부 override 도 가능).
export const MESSAGE_ATTACHMENT_PHOTO_ONLY = false;
const PHOTO_ONLY_NOTICE = "사진만 첨부할 수 있어요.";

const formatBytes = (bytes) => {
    const value = Number(bytes) || 0;
    if (value <= 0) return "";
    const units = ["B", "KB", "MB", "GB"];
    const exponent = Math.min(units.length - 1, Math.floor(Math.log(value) / Math.log(1024)));
    const size = value / 1024 ** exponent;
    return `${exponent === 0 ? size : size.toFixed(1)} ${units[exponent]}`;
};

const statusMeta = {
    PENDING: { label: "요청 대기중", className: "pending" },
    CONFIRMED: { label: "예약 확정", className: "confirmed" },
    DECLINED: { label: "거절됨", className: "declined" },
};

export const Avatar = ({ initials, imageUrl, alt, ring = false, size = "md" }) => {
    const [imageFailed, setImageFailed] = React.useState(false);
    // 공백뿐인 URL 은 깨진 <img> 대신 기본 프로필로 떨어지도록 빈 값 취급한다.
    const resolvedUrl = typeof imageUrl === "string" ? imageUrl.trim() : "";
    const className = `message-avatar message-avatar--${size} ${ring ? "message-avatar--ring" : ""}`;

    React.useEffect(() => {
        setImageFailed(false);
    }, [resolvedUrl]);

    if (resolvedUrl && !imageFailed) {
        // alt 가 있으면 상호작용/가시 맥락 → 의미 있는 대체텍스트, 없으면 장식 이미지.
        const decorative = !alt;
        return (
            <span className={className}>
                <img
                    className="message-avatar__img"
                    src={resolvedUrl}
                    alt={decorative ? "" : alt}
                    aria-hidden={decorative ? "true" : undefined}
                    loading="lazy"
                    onError={() => setImageFailed(true)}
                />
            </span>
        );
    }

    // 프로필 이미지가 없거나 로드 실패 → 글자 없는 기본 프로필 UI.
    return (
        <div
            className={`${className} message-avatar--fallback`}
            data-testid="message-avatar-fallback"
            aria-hidden="true"
        />
    );
};

export const MessageListRow = ({ room, onClick }) => (
    <button className="message-row" type="button" onClick={onClick} aria-label={`${room.title} 메시지방 열기`}>
        <span className="message-row__avatar">
            <Avatar initials={room.avatar} size="lg" imageUrl={room.peer?.profileImageUrl} alt={room.peer?.profileImageUrl ? `${room.title} 프로필 사진` : undefined} />
            <span className="message-row__badge" aria-hidden="true">{getMessageTypeLabel(room).slice(0, 1)}</span>
        </span>
        <span className="message-row__body">
            <span className="message-row__top">
                <strong>{room.title}</strong>
                <time>{room.lastMessageAt}</time>
            </span>
            <span className="message-row__bottom">
                <span className={room.unread > 0 ? "is-unread" : ""}>{room.lastMessage}</span>
                {room.unread > 0 && (
                    <b aria-label={`읽지 않은 메시지 ${room.unread}개`}>
                        <img src={alertIcon} alt="" />
                        {room.unread}
                    </b>
                )}
            </span>
        </span>
    </button>
);

export const MessageDayDivider = ({ children }) => (
    <div className="message-day"><span>{children}</span></div>
);

export const MessageSystemNotice = ({ children }) => (
    <div className="message-system"><span>✓ {children}</span></div>
);

export const BookingCard = ({ message, mine = false }) => {
    const meta = statusMeta[message.status] || statusMeta.PENDING;
    const titleLabel = message.status === "CONFIRMED" ? "수업 예약" : "수업 요청";

    return (
        <article className={`message-booking-card ${mine ? "is-mine" : ""}`} aria-label={`${titleLabel} 카드`}>
            <header>
                <small>📋 {titleLabel}</small>
                <strong>{message.title}</strong>
            </header>
            <div className="message-booking-card__body">
                <dl>
                    <div><dt>📅</dt><dd>{message.date} · {message.timeRange}</dd></div>
                    <div><dt>📍</dt><dd>{message.place}</dd></div>
                    <div><dt>🎫</dt><dd>{message.ticket}</dd></div>
                </dl>
                <footer>
                    <span className={`message-status message-status--${meta.className}`}>{meta.label}</span>
                    <button type="button">상세 ›</button>
                </footer>
            </div>
        </article>
    );
};

export const PlaceCard = ({ message, mine = false }) => (
    <article className={`message-place-card ${mine ? "is-mine" : ""}`} aria-label="장소 공유 카드">
        <div className="message-place-card__map"><span>⌖</span></div>
        <div className="message-place-card__body">
            <strong>{message.name}</strong>
            <span>{message.area}</span>
        </div>
    </article>
);

// 저장 전용 URL 해석. `disposition=attachment` SAS 는 Azure 가 Content-Disposition/Content-Type 을
// 서명에 넣어줘 브라우저가 실제 다운로드로 처리한다. 보기용(inline) URL 과 절대 섞지 않는다.
const resolveDownloadUrl = async (attachment) => {
    // id 가 없으면 저장용 SAS 를 받을 방법이 없다. 남아 있는 attachment.url 은 보기용(또는 출처 불명)이라
    // cross-origin 에서 download 속성이 무시돼 같은 탭 이탈로 이어진다. 계약을 우회하느니 저장하지 않는다.
    if (!attachment.id) return "";
    try {
        return (await getAttachmentDownloadUrl(attachment.id, { disposition: "attachment" })) || "";
    } catch {
        return "";
    }
};

// native saveMedia 브리지에 넘길 첨부 메타데이터. attachmentKey 는 콜백이 그대로 되돌려 주는
// 신원이므로 첨부 id 를 그대로 쓴다(어댑터가 형식만 검증하고 변형하지 않는다).
// mimeType 은 네이티브가 저장 위치(사진첩/파일)를 고르는 근거다.
const saveMetaOf = (attachment) => ({
    attachmentKey: attachment.id == null ? "" : String(attachment.id),
    mimeType: attachment.contentType || "",
});

// 저장 상태 → 파일 행 안내 문구 { status, alert }. 네이티브만 결과를 알려주므로 웹 fallback(anchor) 은 침묵한다.
//   status(role=status): "pending-long" 저장 중 / "pending-unknown" 결과 미수신(잠금 유지) / 성공·미확인 결과
//   alert(role=alert):   영구·일시 실패 결과
//   { status: "pending" } 은 버튼 잠금만, 취소는 둘 다 비운다.
const saveStatusText = (state) => {
    if (!state) return { status: "", alert: "" };
    if (state.status === "pending-long") return { status: NATIVE_SAVE_PENDING_MESSAGE, alert: "" };
    if (state.status === "pending-unknown") return { status: NATIVE_SAVE_UNKNOWN_MESSAGE, alert: "" };
    if (state.status !== "done") return { status: "", alert: "" };
    const described = describeNativeSaveResult(state.result);
    if (!described) return { status: "", alert: "" };
    const isFailure = described.kind === "permanent" || described.kind === "transient";
    return { status: isFailure ? "" : described.message, alert: isFailure ? described.message : "" };
};

// 보기(inline) URL 을 지연 해석한다. attachment.url 이 이미 있으면 그대로 쓰고,
// 없으면 GET /attachments/:id/download-url 로 보충한다(disposition 없음 = inline).
// rebaseUrl = 부모가 **재발급으로** 새로 받아낸 URL 만 담는 단방향 신호.
// 부모가 재발급에 성공했는데 자식이 옛 URL 의 실패에 갇혀 있으면 목록의 그 자리만
// 영영 실패 타일로 남는다. 자식이 스스로 해석해 올린 값은 여기에 담기지 않는다 —
// 담으면 부모가 그 값을 다시 내려보내 자식을 되돌리는 진동이 생긴다.
const useAttachmentUrl = (attachment, rebaseUrl) => {
    const [url, setUrl] = React.useState(attachment.url || "");
    // 실패는 boolean 이 아니라 "그 URL 의 실패"다. 어떤 리소스로 실패했는지 함께 들고 있어야
    // URL 이 교체된 뒤 늦게 도착한 옛 오류를 새 URL 의 실패로 착각하지 않는다.
    // (초기 해석 실패는 "", 로드 실패는 그 렌더의 URL, 재발급 실패는 대체하려던 기존 URL)
    const [failure, setFailure] = React.useState(null);

    const fail = React.useCallback((resource) => setFailure({ resource: resource || "" }), []);
    const clearFailure = React.useCallback(() => setFailure(null), []);

    // force = 재시도. 이미 들고 있는 URL 이 바로 그 실패한 URL 이므로 캐시를 건너뛴다.
    // (SAS 만료·CDN 오류는 URL 을 다시 받아야만 풀린다.)
    const resolve = React.useCallback(async ({ force = false } = {}) => {
        if (!force && url) return url;
        if (!attachment.id) {
            // 재발급할 방법이 없다. 조용히 원래대로 돌아가면 재시도가 먹힌 것처럼 보인다.
            if (force) fail(url);
            return "";
        }
        try {
            const next = await getAttachmentDownloadUrl(attachment.id);
            if (!next) {
                fail(url);
                return "";
            }
            setUrl(next);
            return next;
        } catch {
            fail(url);
            return "";
        }
    }, [attachment.id, url, fail]);

    // 신호가 **실제로 바뀐** 순간에만 따라간다. url 을 deps 에 넣지 않는 것이 핵심이다 —
    // 넣으면 자식의 url 변화가 다시 이 effect 를 깨워 되돌림이 반복된다.
    const lastRebaseRef = React.useRef(rebaseUrl);
    React.useEffect(() => {
        if (rebaseUrl === lastRebaseRef.current) return;
        lastRebaseRef.current = rebaseUrl;
        if (!rebaseUrl) return;
        setUrl(rebaseUrl);
        setFailure(null);
    }, [rebaseUrl]);

    return { url, failure, fail, clearFailure, resolve };
};

// 뷰어 state 는 MessageAttachmentList 가 소유한다(한 메시지의 미디어를 하나의 뷰어로 묶기 위해).
// 개별 첨부는 인라인 렌더·로딩/오류·파일 저장만 책임지고, 열기 요청과 확보한 URL 은 위로 올린다.
export const MessageAttachment = ({
    attachment,
    onMediaLoad,
    onOpen,
    onResolveUrl,
    onMediaError,
    onRetry,
    canRetry = true,
    rebaseUrl,
    photoOnly = MESSAGE_ATTACHMENT_PHOTO_ONLY,
}) => {
    const { url, failure, fail, clearFailure, resolve } = useAttachmentUrl(attachment, rebaseUrl);
    const error = failure !== null;
    const [saving, setSaving] = React.useState(false);
    // 파일 첨부 버튼 전용 저장 상태 { status: "pending" | "pending-long" | "done", result? }.
    // 크기 라벨과 별도 슬롯에 표시한다 — 저장 결과가 크기나 링크 오류를 가리지 않는다.
    const [saveStatus, setSaveStatus] = React.useState(null);
    const [retrying, setRetrying] = React.useState(false);
    const retryingRef = React.useRef(false);
    // 재시도 컨트롤이 열기 컨트롤로 교체될 때 포커스를 이어주기 위한 참조.
    const retryButtonRef = React.useRef(null);
    const openerRef = React.useRef(null);
    // 재시도가 최종 실패해 컨트롤이 사라지는 자리. 포커스가 갈 곳이 여기밖에 없다.
    const terminalErrorRef = React.useRef(null);
    const restoreFocusRef = React.useRef(false);
    const sizeLabel = formatBytes(attachment.sizeBytes);

    React.useEffect(() => {
        // 이미지·영상은 미리 URL 을 확보해 인라인 렌더한다.
        if (attachment.kind === "file") return;
        if (url) return;
        resolve();
    }, [attachment.kind, url, resolve]);

    // 부모 콜백은 렌더마다 신원이 바뀔 수 있다. URL 이 실제로 바뀔 때만 위로 알린다.
    // ref 대입은 렌더 본문이 아니라 effect 에서 한다 — 렌더 중 변이는 concurrent/StrictMode
    // 에서 버려진 렌더의 값을 남길 수 있다. 아래 알림 effect 보다 먼저 선언해야 순서가 맞다.
    const onResolveUrlRef = React.useRef(onResolveUrl);
    React.useEffect(() => {
        onResolveUrlRef.current = onResolveUrl;
    });
    React.useEffect(() => {
        if (url) onResolveUrlRef.current?.(url);
    }, [url]);

    // 실패는 부모까지 올린다 — 인라인 표시만 바꾸면 부모가 만드는 뷰어 슬라이드에는
    // 그대로 남아 빈 슬라이드가 된다. URL 해석 실패(useAttachmentUrl)와 실제 로드 실패
    // (img/video onError)가 같은 error 로 모이므로 한 곳에서 알린다.
    const onMediaErrorRef = React.useRef(onMediaError);
    React.useEffect(() => {
        onMediaErrorRef.current = onMediaError;
    });
    React.useEffect(() => {
        // file 은 뷰어 슬라이드가 아니다. 다운로드 실패를 미디어 실패로 올리면
        // 부모가 슬라이드도 아닌 key 를 실패 목록에 쌓는다.
        if (failure && attachment.kind !== "file") onMediaErrorRef.current?.(failure.resource);
    }, [failure, attachment.kind]);

    // 재시도가 성공해 컨트롤이 교체되면, 그 재시도를 시작한 포커스를 새 열기 버튼으로 옮긴다.
    // 옮기지 않으면 키보드 사용자는 사라진 버튼 자리에서 포커스를 잃는다.
    React.useEffect(() => {
        if (!restoreFocusRef.current) return;
        if (failure || !url) return;
        restoreFocusRef.current = false;
        openerRef.current?.focus();
    }, [failure, url]);

    // 재시도가 최종 실패하면 버튼 자체가 사라진다. 그 재시도를 시작한 포커스를
    // 실패 안내로 옮기지 않으면 body 로 떨어져 무슨 일이 있었는지도 낭독되지 않는다.
    // 진행 중(retrying)에는 아직 버튼이 남아 있으므로 건드리지 않는다.
    React.useEffect(() => {
        if (!restoreFocusRef.current) return;
        if (!failure || canRetry || retrying) return;
        restoreFocusRef.current = false;
        terminalErrorRef.current?.focus();
    }, [failure, canRetry, retrying]);

    // 이미지·동영상은 같은 구조다: 본문 선택 = 보기(공통 뷰어), 저장 = 뷰어 안의 저장 버튼.
    // 인라인 저장 링크는 역할이 겹치므로 두지 않는다.
    // 재발급은 자식이 직접 한다. 부모가 같이 요청하면 같은 SAS 를 두 번 받게 된다.
    // 부모에게는 "이 key 의 실패를 비워라"만 알린다.
    // 잠금은 state 가 아니라 ref 로 건다 — 같은 tick 에 연달아 눌리면 retrying 이
    // 아직 반영되기 전이라 두 번째 클릭이 그대로 통과한다.
    const handleRetry = async () => {
        if (retryingRef.current) return;
        retryingRef.current = true;
        // 이 컨트롤에 포커스를 둔 채 시작했을 때만(=키보드 경로) 나중에 포커스를 이어준다.
        // 포인터로 눌렀거나 포커스가 다른 곳에 있으면 빼앗지 않는다.
        restoreFocusRef.current = document.activeElement === retryButtonRef.current;
        setRetrying(true);
        onRetry?.();
        try {
            const next = await resolve({ force: true });
            // 실패면 resolve 가 실패 리소스와 함께 failure 를 다시 세워 부모에도 알린다.
            // 실패해도 플래그는 유지한다 — 갈 곳이 열기 버튼이 아니라 실패 안내로 바뀔 뿐이다.
            if (next) clearFailure();
        } finally {
            retryingRef.current = false;
            setRetrying(false);
        }
    };

    if (attachment.kind === "image" || attachment.kind === "video") {
        const isVideo = attachment.kind === "video";

        if (error) {
            const failureText = isVideo ? "동영상을 불러올 수 없어요" : "이미지를 불러올 수 없어요";
            // 재시도가 남아 있으면 오류 자리 전체가 하나의 버튼이다. 안쪽에 별도 버튼을 두면
            // `button.message-attachment-media` reset(테두리·패딩·cursor)이 걸리지 않는다.
            // 진행 중에는 canRetry 가 이미 소진으로 바뀌어 있어도 버튼을 유지한다 —
            // 결과가 나오기 전에 컨트롤이 사라지면 무엇이 진행 중인지 알 수 없다.
            if (canRetry || retrying) {
                return (
                    <button
                        type="button"
                        ref={retryButtonRef}
                        className="message-attachment-media message-attachment-media--error"
                        onClick={handleRetry}
                        // native disabled 는 포커스를 body 로 날린다. 잠금은 aria 로 알리고
                        // 중복 실행은 retryingRef 동기 guard 가 막는다.
                        aria-disabled={retrying ? "true" : "false"}
                        aria-busy={retrying ? "true" : "false"}
                        aria-label={`${attachment.filename} 불러오기 실패, 다시 시도`}
                    >
                        {`${failureText}. 다시 시도`}
                    </button>
                );
            }
            // 재시도를 소진한 뒤에는 컨트롤을 두지 않는다 —
            // 눌러도 아무 일이 없는 버튼보다 명시적인 비대화형 실패가 정직하다.
            return (
                <span
                    ref={terminalErrorRef}
                    className="message-attachment-media message-attachment-media--error"
                    // 가짜 버튼은 두지 않는다. 대신 포커스만 받을 수 있게 해 두고,
                    // status 로 무엇이 실패했는지(파일명 + 불러오기 실패) 읽히게 한다.
                    tabIndex={-1}
                    role="status"
                    aria-label={`${attachment.filename} 불러오기 실패`}
                >
                    {failureText}
                </span>
            );
        }
        if (!url) {
            return <span className="message-attachment-media message-attachment-media--loading" aria-label={`${attachment.filename} 불러오는 중`} />;
        }

        return (
            <div className="message-attachment-media-wrap">
                {/* 새 탭 이탈(target=_blank)·인라인 컨트롤 대신 공통 뷰어를 연다. */}
                <button
                    ref={openerRef}
                    className={`message-attachment-media${isVideo ? " message-attachment-media--video" : ""}`}
                    type="button"
                    onClick={() => onOpen?.()}
                    aria-label={`${attachment.filename} ${isVideo ? "동영상" : "이미지"} 열기`}
                >
                    {isVideo ? (
                        <>
                            {/* 썸네일은 바깥 버튼의 장식이다. 여기에 aria-label 을 두면
                                스크린리더가 같은 첨부를 "열기 버튼 + 동영상"으로 두 번 읽는다. */}
                            <video
                                // URL 이 바뀌면 요소를 갈아 끼운다. 같은 DOM 을 재사용하면
                                // 옛 URL 의 늦은 error 가 새 URL 의 실패로 기록된다.
                                key={url}
                                src={url}
                                data-testid="message-attachment-video"
                                preload="metadata"
                                muted
                                playsInline
                                aria-hidden="true"
                                onLoadedMetadata={() => onMediaLoad?.()}
                                onError={() => fail(url)}
                            />
                            <span className="message-attachment-play" aria-hidden="true">▶</span>
                        </>
                    ) : (
                        <img key={url} src={url} alt={attachment.filename} loading="lazy" onLoad={() => onMediaLoad?.()} onError={() => fail(url)} />
                    )}
                </button>
            </div>
        );
    }

    // 사진-only(dev): 일반파일은 다운로드를 제공하지 않는다. 눌러도 아무 일이 없는 버튼 대신
    // 파일명·크기만 보이는 비대화형 행을 둔다(기존 메시지는 그대로 남는다).
    if (photoOnly) {
        return (
            <div className="message-attachment-file message-attachment-file--static">
                <span className="message-attachment-file__icon" aria-hidden="true">📎</span>
                <span className="message-attachment-file__meta">
                    <strong>{attachment.filename}</strong>
                    {sizeLabel && <em>{sizeLabel}</em>}
                    {/* F3: 왜 열리지 않는지 보이는 안내. 버튼 행과 시각·낭독 모두에서 구분된다. */}
                    <em className="message-attachment-file__hint">다운로드 미제공</em>
                </span>
            </div>
        );
    }

    // 파일 첨부도 새 탭으로 이탈하지 않는다. 클릭할 때마다 attachment SAS 를 재발급해
    // 이미지·동영상과 같은 어댑터로 저장한다. 진행 중에는 버튼을 잠가 중복 요청을 막는다.
    const handleFileDownload = async () => {
        if (saving) return;
        setSaving(true);
        clearFailure();
        setSaveStatus({ status: "pending" });
        try {
            const target = await resolveDownloadUrl(attachment);
            if (!target) {
                // 링크 오류가 이전 저장 결과를 대체한다 — 둘을 같이 보이면 "저장했어요" 가 링크 오류를 가린다.
                setSaveStatus(null);
                fail("");
                return;
            }
            const result = await downloadMedia(target, attachment.filename, {
                ...saveMetaOf(attachment),
                onPending: () => setSaveStatus({ status: "pending-long" }),
                // 15분 경계 — 종결이 아니다. 안내만 바꾸고 잠금(saving)은 실제 결과까지 유지한다.
                onUnknown: () => setSaveStatus({ status: "pending-unknown" }),
            });
            if (result && result.via === "none") {
                setSaveStatus(null);
                fail("");
                return;
            }
            setSaveStatus({ status: "done", result });
        } finally {
            setSaving(false);
        }
    };

    const saveText = saveStatusText(saveStatus);
    // 링크 오류(저장용 SAS 확보 실패)도 실패이므로 alert 노드에 싣는다. 저장 실패와 동시에 나지 않는다.
    const alertText = error ? "다운로드 링크를 가져오지 못했어요" : saveText.alert;

    return (
        <button
            className="message-attachment-file"
            type="button"
            onClick={handleFileDownload}
            disabled={saving}
            // aria-busy 를 두지 않는다 — busy 조상 아래의 live region 안내(저장 중·결과 미수신)는
            // 보조기기가 busy 가 풀릴 때까지 미룰 수 있다. 중복 차단은 disabled 와 saving 이 맡는다.
            aria-label={`${attachment.filename} 파일 다운로드`}
        >
            <span className="message-attachment-file__icon" aria-hidden="true">📎</span>
            <span className="message-attachment-file__meta">
                <strong>{attachment.filename}</strong>
                {sizeLabel && <em>{sizeLabel}</em>}
                {/* 안내는 서로 독립된 두 live region 에 싣는다(status: 저장 중·결과 미수신·성공 / alert: 링크 오류·저장 실패).
                    둘 다 처음부터 빈 채로 마운트돼 있고 role 도 바뀌지 않는다 — 내용이 담긴 채 새로 마운트되거나
                    role 이 바뀌는 노드, alert 가 status 안에 중첩된 구조는 보조기기가 안내를 놓치기 쉽다.
                    비어 있을 때는 flex 흐름에서 빼(absolute) 세로 gap 을 만들지 않되, display/visibility 로 숨기지는
                    않는다(접근성 트리에 남아야 다음 안내가 들린다). 크기 라벨은 결과와 무관하게 남는다. */}
                <em
                    className="message-attachment-file__save"
                    role="status"
                    aria-label={`${attachment.filename} 저장 상태`}
                    style={saveText.status ? undefined : { position: "absolute" }}
                >
                    {saveText.status}
                </em>
                <em
                    className="message-attachment-file__save"
                    role="alert"
                    aria-label={`${attachment.filename} 저장 오류`}
                    style={alertText ? undefined : { position: "absolute" }}
                >
                    {alertText}
                </em>
            </span>
        </button>
    );
};

const attachmentKey = (attachment, index) => attachment.id ?? `idx-${index}`;

// 뷰어를 첨부 목록 자리(.message-room__body 안, App 의 PullToRefresh 스크롤러 내부)에 그대로
// 렌더하면 iOS 앱에서 채팅 헤더·입력바가 뷰어 위에 그려지고 저장·닫기 탭이 뷰어에 닿지 않는
// 증상이 관측됐다. 뷰어를 채팅방 트리 밖(document.body)에 두어 헤더·입력바와 같은 스택
// 안에서 겹치지 않게 한다 — 채팅 시트(MessageRoomPeerActions)와 같은 방식이다. 공통
// MediaViewer 는 그대로 두고, document 가 없는 환경은 기존처럼 인라인 렌더로 폴백한다.
const portalToBody = (node) =>
    typeof document !== "undefined" && document.body ? createPortal(node, document.body) : node;

export const MessageAttachmentList = ({ attachments = [], onMediaLoad, photoOnly = MESSAGE_ATTACHMENT_PHOTO_ONLY }) => {
    const [resolvedUrls, setResolvedUrls] = React.useState({});
    // 보기용 URL 확보나 실제 로드에 실패한 첨부의 key. 슬라이드로 삼을 수 없다.
    const [failedKeys, setFailedKeys] = React.useState({});
    // 뷰어를 여는 시점에 **슬라이드 key 순서를 통째로 고정**한다.
    // 열린 뒤에도 묶음이 변하면 Swiper 는 위치 기반이라 보고 있던 자리가 다른 미디어를
    // 가리키게 되고, 그걸 보정하려면 현재 위치를 되묻는 왕복이 생긴다(그 index 통지는
    // 사용자 조작과 내부 update 를 구분할 수 없어 앵커가 어긋날 여지가 남는다).
    // 순서를 고정하면 보정 자체가 필요 없다.
    const [viewer, setViewer] = React.useState(null);

    const handleResolveUrl = React.useCallback((key, url) => {
        setResolvedUrls((prev) => (prev[key] === url ? prev : { ...prev, [key]: url }));
    }, []);

    const markFailed = React.useCallback((key) => {
        setFailedKeys((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
    }, []);

    // 첨부 순서를 유지한 채 image/video 의 원본 대응을 만든다.
    // 자리(멤버십)는 **URL 해석 여부와 무관하게** 첨부 자체로 정한다 — URL 로 정하면
    // 늦게 온 형제가 슬라이드를 삽입해 위치가 밀린다. url 은 나중에 채워 넣는다.
    const mediaEntries = React.useMemo(
        () =>
            attachments
                .map((attachment, index) => ({ attachment, key: attachmentKey(attachment, index) }))
                .filter(({ attachment }) => attachment.kind === "image" || attachment.kind === "video"),
        [attachments],
    );

    const entryByKey = React.useMemo(
        () => new Map(mediaEntries.map((entry) => [entry.key, entry])),
        [mediaEntries],
    );

    // 재발급 대기 중인 key. 그동안은 캐시도 attachment.url 도 쓰지 않는다 —
    // 둘 다 방금 실패한 그 URL 이라 다시 그리면 같은 실패를 반복한다.
    const retryPendingRef = React.useRef({});
    const [retryPendingKeys, setRetryPendingKeys] = React.useState({});
    // 부모 재발급으로 새로 받아낸 URL 만 담는다. 자식이 올린 값은 절대 담지 않는다
    // (담으면 자식 → 부모 → 자식 으로 되돌아가는 진동이 된다).
    const [retryResolvedUrls, setRetryResolvedUrls] = React.useState({});

    const beginRetry = (key) => {
        retryPendingRef.current[key] = true;
        setRetryPendingKeys((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
    };

    const endRetry = (key) => {
        delete retryPendingRef.current[key];
        setRetryPendingKeys((prev) => {
            if (!prev[key]) return prev;
            const next = { ...prev };
            delete next[key];
            return next;
        });
    };

    // 미디어가 아닌 첨부(file)는 슬라이드가 없다. 재발급 대기 중 도착한 오류는
    // 방금 버린 URL 의 것이므로 이 자리를 실패로 고정하지 못하게 막는다.
    const handleMediaError = (key, failedResource) => {
        if (!entryByKey.has(key)) return;
        if (retryPendingRef.current[key]) return;
        // 실패는 그 URL 의 실패다. 지금 이 자리의 URL 과 다르면 이미 교체된 리소스의 오류다.
        if (failedResource !== undefined && failedResource !== urlForKey(key)) return;
        markFailed(key);
    };

    const urlForKey = (key) =>
        retryPendingKeys[key] ? "" : resolvedUrls[key] || entryByKey.get(key)?.attachment.url || "";

    // 실패로 표시된 첨부를 다시 누르는 것은 "다시 시도해 달라"는 뜻이다.
    // 다만 실패가 반복될 때마다 왕복하지 않도록 key 당 한 번만 재발급한다.
    // ref 는 같은 tick 의 중복 클릭까지 막고, state 는 소진 여부를 화면에 반영한다.
    const retriedKeysRef = React.useRef({});
    const [retriedKeys, setRetriedKeys] = React.useState({});

    const claimRetry = (key) => {
        if (retriedKeysRef.current[key]) return false;
        retriedKeysRef.current[key] = true;
        setRetriedKeys((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
        return true;
    };

    // 이 key 의 실패 표시와 캐시된 URL 만 비운다. 재발급 요청은 하지 않는다.
    const clearFailure = (key) => {
        setFailedKeys((prev) => {
            if (!prev[key]) return prev;
            const next = { ...prev };
            delete next[key];
            return next;
        });
        setResolvedUrls((prev) => {
            if (!(key in prev)) return prev;
            const next = { ...prev };
            delete next[key];
            return next;
        });
    };

    // 인라인 실패의 재시도는 자식이 URL 을 다시 받아온다. 부모는 실패 표시만 비운다.
    // 캐시는 지우지 않는다 — 자식이 새 URL 을 올려주면 그때 덮인다. 미리 비우면
    // 재발급 결과가 같은 URL 일 때 자리가 빈 채로 남는다.
    const handleInlineRetry = (key) => {
        claimRetry(key);
        setFailedKeys((prev) => {
            if (!prev[key]) return prev;
            const next = { ...prev };
            delete next[key];
            return next;
        });
    };

    const retryResolveUrl = async (key, attachment) => {
        // id 가 없으면 재발급할 방법이 없다. 남아 있는 attachment.url 로만 열 수 있다.
        if (!attachment?.id) {
            endRetry(key);
            if (!attachment?.url) markFailed(key);
            return;
        }
        try {
            const next = await getAttachmentDownloadUrl(attachment.id);
            if (next) {
                handleResolveUrl(key, next);
                // 자식도 이 URL 기준으로 다시 맞추라는 단방향 신호.
                setRetryResolvedUrls((prev) => (prev[key] === next ? prev : { ...prev, [key]: next }));
                // 성공은 실패를 명시적으로 푼다 — 대기 중 도착한 이전 URL 의 오류가
                // 이미 기록돼 있을 수 있다.
                setFailedKeys((prev) => {
                    if (!prev[key]) return prev;
                    const rest = { ...prev };
                    delete rest[key];
                    return rest;
                });
                endRetry(key);
                return;
            }
            endRetry(key);
            markFailed(key);
        } catch {
            endRetry(key);
            markFailed(key);
        }
    };

    const openViewer = (key) => {
        let excluded = failedKeys;
        if (failedKeys[key]) {
            if (!claimRetry(key)) {
                // 재시도를 이미 소진했다. 무반응으로 삼키는 대신 그 자리를 실패 슬라이드로
                // 열어 무엇이 실패했는지 보이고, 형제 미디어로는 계속 넘어갈 수 있게 한다.
                const keys = mediaEntries
                    .map((entry) => entry.key)
                    .filter((k) => k === key || !failedKeys[k]);
                const index = keys.indexOf(key);
                if (index < 0) return;
                setViewer({ keys, index });
                return;
            }
            // 이 key 만 실패에서 풀어준다. 나머지 실패·첨부 순서·저장 매핑은 그대로다.
            excluded = { ...failedKeys };
            delete excluded[key];
            clearFailure(key);
            beginRetry(key);
            retryResolveUrl(key, entryByKey.get(key)?.attachment);
        }
        // 실패한 항목은 애초에 슬라이드에 넣지 않는다. 순서는 여기서 확정된다.
        const keys = mediaEntries.map((entry) => entry.key).filter((k) => !excluded[k]);
        const index = keys.indexOf(key);
        if (index < 0) return;
        setViewer({ keys, index });
    };

    // 고정된 key 순서로 원본 첨부를 찾아 그 id 로 저장용 SAS 를 매번 재발급한다.
    // 저장 상태는 뷰어가 아니라 목록이 첨부 key 별로 소유한다. 뷰어는 열고 닫을 때마다 다시 마운트되므로,
    // 닫혀 있는 동안 도착한 결과와 아직 진행 중인 요청을 잃지 않으려면 여기 있어야 한다.
    const [saveStates, setSaveStates] = React.useState({});
    const setSaveState = (key, state) => setSaveStates((prev) => ({ ...prev, [key]: state }));
    // 같은 tick 의 연타는 state 반영 전이라 ref 로 막는다.
    const savingKeysRef = React.useRef(new Set());

    const handleDownload = async (_slide, index) => {
        const key = viewer?.keys[index];
        const entry = entryByKey.get(key);
        if (!entry) return { via: "none" };
        // 사진-only(dev): 영상 저장 요청은 버튼이 없어 오지 않지만, 경계에서 한 번 더 막는다.
        if (photoOnly && entry.attachment.kind === "video") return { via: "none" };
        // 결과를 모르는 동안(네이티브 대기 포함) 같은 첨부를 다시 요청하지 않는다.
        if (savingKeysRef.current.has(key)) return null;
        savingKeysRef.current.add(key);
        setSaveState(key, { status: "pending" });
        try {
            const target = await resolveDownloadUrl(entry.attachment);
            // 결과를 반드시 돌려준다 — 뷰어 없이 호출된 경우와 기존 테스트가 이 값을 본다.
            if (!target) {
                setSaveState(key, { status: "done", result: { via: "none" } });
                return { via: "none" };
            }
            const result = await downloadMedia(target, entry.attachment.filename, {
                ...saveMetaOf(entry.attachment),
                onPending: () => setSaveState(key, { status: "pending-long" }),
                // 15분 경계 — 종결이 아니다. 잠금은 실제 결과까지 유지된다(pending-unknown 도 pending 취급).
                onUnknown: () => setSaveState(key, { status: "pending-unknown" }),
            });
            // 결과는 요청한 key 에만 기록한다 — 뷰어가 다른 슬라이드를 보고 있어도 잘못 붙지 않는다.
            setSaveState(key, { status: "done", result });
            return result;
        } finally {
            savingKeysRef.current.delete(key);
        }
    };

    if (!attachments.length) return null;

    return (
        <div className="message-attachment-list">
            {attachments.map((attachment, index) => {
                const key = attachmentKey(attachment, index);
                return (
                    <MessageAttachment
                        key={key}
                        attachment={attachment}
                        onMediaLoad={onMediaLoad}
                        onOpen={() => openViewer(key)}
                        onResolveUrl={(url) => handleResolveUrl(key, url)}
                        onMediaError={(failedResource) => handleMediaError(key, failedResource)}
                        rebaseUrl={retryResolvedUrls[key]}
                        onRetry={() => handleInlineRetry(key)}
                        canRetry={!retriedKeys[key]}
                        photoOnly={photoOnly}
                    />
                );
            })}
            {viewer && portalToBody(
                <MediaViewer
                    media={viewer.keys.map((key) => {
                        const kind = entryByKey.get(key)?.attachment.kind;
                        const slide = {
                            url: urlForKey(key),
                            type: kind === "video" ? "video" : "image",
                            // 뷰어가 저장 요청을 귀속시킬 신원. url 은 재발급되면 바뀌므로
                            // 자리(index)도 url 도 아닌 attachment key 가 기준이어야 한다.
                            key,
                        };
                        // 여는 시점에 아직 URL 이 없던 형제의 해석이 뒤늦게 실패하면 채울 url 이
                        // 영영 오지 않는다. 자리는 지키되 영구 로딩이 아니라 실패로 알린다.
                        if (failedKeys[key]) slide.failed = true;
                        // 사진-only(dev): 영상은 보기·재생만, 저장 버튼은 이 슬라이드에서 감춘다.
                        if (photoOnly && kind === "video") slide.downloadable = false;
                        return slide;
                    })}
                    initialIndex={viewer.index}
                    // 한 장뿐이면 파일명이 더 유용하고, 여러 장이면 뷰어 기본 카운터를 쓴다.
                    title={
                        viewer.keys.length === 1
                            ? entryByKey.get(viewer.keys[0])?.attachment.filename
                            : undefined
                    }
                    onClose={() => setViewer(null)}
                    // 열려 있는 동안 실패한 슬라이드는 자리를 지킨 채 실패 상태로 바뀐다(뷰어 담당).
                    // 여기서는 그 사실만 기억해 다음에 열 때 슬라이드에서 제외한다.
                    onSlideError={(index, failedResource) => handleMediaError(viewer.keys[index], failedResource)}
                    onDownload={handleDownload}
                    saveStates={saveStates}
                />,
            )}
        </div>
    );
};

const URL_PATTERN = /(https?:\/\/[^\s<>"']+)/gi;
// 문장 끝 구두점·닫는 괄호는 링크에서 제외한다.
const TRAILING_PUNCTUATION = /[.,!?;:)\]}]+$/;

// 텍스트 내 http(s) URL을 새 창으로 여는 링크로 변환한다.
const LinkifiedText = ({ text }) => {
    const parts = text.split(URL_PATTERN);
    return parts.map((part, index) => {
        if (index % 2 === 0) return part ? <React.Fragment key={index}>{part}</React.Fragment> : null;
        const trailing = part.match(TRAILING_PUNCTUATION)?.[0] || "";
        const url = trailing ? part.slice(0, -trailing.length) : part;
        return (
            <React.Fragment key={index}>
                <a className="message-message__link" href={url} target="_blank" rel="noopener noreferrer">
                    {url}
                </a>
                {trailing}
            </React.Fragment>
        );
    });
};

const LONG_PRESS_MS = 500;
const LONG_PRESS_MOVE_TOLERANCE = 10;

// SnackbarProvider 밖(단위 테스트 등)에서도 렌더링될 수 있어 없으면 null 로 둔다.
const useOptionalSnackbar = () => {
    try {
        return useSnackbar();
    } catch {
        return null;
    }
};

const copyToClipboard = async (text) => {
    if (navigator.clipboard?.writeText) {
        try {
            await navigator.clipboard.writeText(text);
            return true;
        } catch {
            // 권한/보안 컨텍스트 문제 시 아래 fallback 으로 진행
        }
    }
    // 일부 WebView 는 Clipboard API 를 지원하지 않아 execCommand 로 대체한다.
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    try {
        return document.execCommand("copy");
    } catch {
        return false;
    } finally {
        document.body.removeChild(textarea);
    }
};

// Android WebView 는 진동을 지원하고, iOS 등 미지원 환경에서는 조용히 무시된다.
const vibrate = (duration) => {
    try {
        navigator.vibrate?.(duration);
    } catch {
        // noop
    }
};

// 말풍선을 길게 누르면 메시지 전체 텍스트를 복사한다.
const MessageText = ({ text }) => {
    const snackbar = useOptionalSnackbar();
    const [isPressing, setIsPressing] = React.useState(false);
    const timerRef = React.useRef(null);
    const startPointRef = React.useRef(null);
    const longPressedRef = React.useRef(false);

    const clearTimer = () => {
        clearTimeout(timerRef.current);
        timerRef.current = null;
        setIsPressing(false);
    };

    React.useEffect(() => clearTimer, []);

    const handlePointerDown = (event) => {
        if (event.pointerType === "mouse" && event.button !== 0) return;
        longPressedRef.current = false;
        startPointRef.current = { x: event.clientX, y: event.clientY };
        clearTimer();
        setIsPressing(true);
        timerRef.current = setTimeout(async () => {
            timerRef.current = null;
            longPressedRef.current = true;
            setIsPressing(false);
            vibrate(15);
            const copied = await copyToClipboard(text);
            if (copied) snackbar?.showSuccess("메시지가 복사되었습니다.");
            else snackbar?.showError("메시지 복사에 실패했습니다.");
        }, LONG_PRESS_MS);
    };

    const handlePointerMove = (event) => {
        const start = startPointRef.current;
        if (!timerRef.current || !start) return;
        const moved = Math.hypot(event.clientX - start.x, event.clientY - start.y);
        if (moved > LONG_PRESS_MOVE_TOLERANCE) clearTimer();
    };

    // 길게 눌러 복사한 직후에는 링크 이동을 막는다.
    const handleClickCapture = (event) => {
        if (!longPressedRef.current) return;
        event.preventDefault();
        event.stopPropagation();
        longPressedRef.current = false;
    };

    return (
        <p
            className={`message-message__text${isPressing ? " is-pressing" : ""}`}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={clearTimer}
            onPointerCancel={clearTimer}
            onPointerLeave={clearTimer}
            onContextMenu={(event) => event.preventDefault()}
            onClickCapture={handleClickCapture}
        >
            <LinkifiedText text={text} />
        </p>
    );
};

export const MessageBubble = ({ message, opponentAvatar, peer, onMediaLoad }) => {
    const navigate = useNavigate();
    const mine = message.from === "me";

    if (message.type === "day") return <MessageDayDivider>{message.label}</MessageDayDivider>;
    if (message.type === "system") return <MessageSystemNotice>{message.text}</MessageSystemNotice>;

    const isRich = message.type === "booking" || message.type === "place";
    const attachments = message.attachments || [];
    const hasText = Boolean(message.text);
    const initials = message.avatar || opponentAvatar;
    const peerName = peer?.nickname || initials;

    return (
        <div className={`message-message ${mine ? "is-mine" : "is-other"}`}>
            {!mine && (
                <div className="message-message__avatar">
                    {peer?.profileUrl ? (
                        <button
                            type="button"
                            className="message-avatar-button"
                            onClick={() => navigate(peer.profileUrl)}
                            aria-label={`${peerName} 프로필 보기`}
                        >
                            <Avatar
                                initials={initials}
                                size="sm"
                                imageUrl={peer.profileImageUrl}
                                alt={`${peerName} 프로필 사진`}
                            />
                        </button>
                    ) : (
                        <Avatar
                            initials={initials}
                            size="sm"
                            imageUrl={peer?.profileImageUrl}
                            alt={peer?.profileImageUrl ? `${peerName} 프로필 사진` : undefined}
                        />
                    )}
                </div>
            )}
            <div className="message-message__content">
                {message.type === "booking" && <BookingCard message={message} mine={mine} />}
                {message.type === "place" && <PlaceCard message={message} mine={mine} />}
                {!isRich && attachments.length > 0 && <MessageAttachmentList attachments={attachments} onMediaLoad={onMediaLoad} />}
                {!isRich && hasText && <MessageText text={message.text} />}
            </div>
            <div className="message-message__meta">
                {mine && message.read && <span>읽음</span>}
                {message.time && <time>{message.time}</time>}
            </div>
        </div>
    );
};

// peerActionsTrigger: MessageRoom 이 만든 점 3개 상대 메뉴 버튼.
// BottomSheet 본체는 header 의 backdrop-filter containing block 에 갇히지 않도록
// MessageRoom 레벨에서 따로 렌더하므로, 여기서는 트리거 버튼만 받아 헤더에 배치한다.
// onBack: 뒤로가기 정책(이전 화면 있으면 이전 화면, 없으면 홈)은 MessageRoom 이 useGoBack 으로 정해 넘긴다.
// 헤더가 navigate(-1) 을 직접 부르면 history 가 없는 진입(푸시 cold-start, 레이어 직접 오픈)에서 무반응이 된다.
export const MessageRoomHeader = ({ room, onBack, peerActionsTrigger = null }) => {
    const navigate = useNavigate();
    const peer = room.peer;
    const profileUrl = peer?.profileUrl;
    const peerName = peer?.nickname || room.title;

    return (
        <header className="message-room-header">
            <button type="button" onClick={onBack} aria-label="뒤로가기">‹</button>
            {profileUrl ? (
                <button
                    type="button"
                    className="message-room-header__peer"
                    onClick={() => navigate(profileUrl)}
                    aria-label={`${peerName} 프로필 보기`}
                >
                    <Avatar initials={room.avatar} imageUrl={peer.profileImageUrl} alt={`${peerName} 프로필 사진`} />
                    <span className="message-room-header__peer-text">
                        <strong id="message-room-title">{room.title}</strong>
                    </span>
                </button>
            ) : (
                <>
                    <Avatar
                        initials={room.avatar}
                        imageUrl={peer?.profileImageUrl}
                        alt={peer?.profileImageUrl ? `${peerName} 프로필 사진` : undefined}
                    />
                    <div>
                        <strong id="message-room-title">{room.title}</strong>
                    </div>
                </>
            )}
            {peerActionsTrigger}
        </header>
    );
};

const attachmentActions = [
    { key: "photo", label: "사진 첨부", icon: "🖼️", hint: "앨범에서 사진 선택", accept: "image/*", kind: "image" },
    { key: "video", label: "동영상 첨부", icon: "🎬", hint: "짧은 영상 공유", accept: "video/*", kind: "video" },
    { key: "file", label: "파일 첨부", icon: "📎", hint: "PDF·문서 파일", accept: "", kind: "file" },
];

const attachmentKindFromFile = (file) => {
    const type = file?.type || "";
    if (type.startsWith("image/")) return "image";
    if (type.startsWith("video/")) return "video";
    return "file";
};

// dev-chat-photo-only-0915 F1: 모바일 picker 가 file.type 을 생략한 파일은 업로드 계층(api/chat.js)이 파일명으로
// MIME 을 추론해 보낸다. 사진-only 판정도 같은 기준을 써야 알려진 사진(.heic/.jpg …)이 거부되지 않는다.
// 명시 MIME 은 그대로 우선하고, 미지 확장자는 application/octet-stream → file 로 계속 차단된다.
// photoOnly 가 아닐 때는 기존 판정(attachmentKindFromFile)을 그대로 써 prod/unknown 동작을 바꾸지 않는다.
const attachmentKindForPolicy = (file, photoOnly) =>
    photoOnly ? attachmentKindFromContentType(inferContentTypeFromFilename(file)) : attachmentKindFromFile(file);

// hotfix-20260628 / dev-message-attachments-0908: 첨부 기능 차단 플래그.
// true면 액션 클릭/파일 input 모두 "서비스 준비중입니다." 안내만 노출하고,
// false면 기존 첨부 경로(선택 콜백·accept·file picker·업로드)가 그대로 복구된다.
//
// 배포 환경별 기본값을 기존 공개 판별자(src/utils/index.js isDevEnv)로 결정한다.
// isDevEnv = 공개 빌드 환경변수 REACT_APP_API_URL 의 apidev 포함 여부 || localhost 호스트.
// dev/로컬 → 해제, 운영 → 차단 유지. 판별자가 없는 환경은 isDevEnv 가 falsy 라 fail-closed 로 차단된다.
// (dev 릴리스도 NODE_ENV=production 으로 빌드되므로 NODE_ENV 는 판별자로 쓸 수 없다.)
const MESSAGE_ATTACHMENT_COMING_SOON = !isDevEnv;

export const MESSAGE_ATTACHMENT_LIMITS = {
    maxCount: 10,
    maxImageBytes: 20 * 1024 * 1024,
    maxVideoBytes: 200 * 1024 * 1024,
    maxFileBytes: 50 * 1024 * 1024,
};

const attachmentLimitForKind = (kind) => {
    if (kind === "image") return MESSAGE_ATTACHMENT_LIMITS.maxImageBytes;
    if (kind === "video") return MESSAGE_ATTACHMENT_LIMITS.maxVideoBytes;
    return MESSAGE_ATTACHMENT_LIMITS.maxFileBytes;
};

const attachmentLimitLabel = (kind) => formatBytes(attachmentLimitForKind(kind));

const validateAttachmentFile = (file, kind) => {
    const limit = attachmentLimitForKind(kind);
    if ((Number(file?.size) || 0) > limit) {
        const label = kind === "image" ? "사진" : kind === "video" ? "동영상" : "파일";
        return `${label}은 파일당 최대 ${attachmentLimitLabel(kind)}까지 첨부할 수 있어요.`;
    }
    return "";
};

export const MessageInputBar = ({ disabled = false, sending = false, inputRef, onSend, onUploadAttachment, onAttachmentSelect, onFocus, onBlur, comingSoon = MESSAGE_ATTACHMENT_COMING_SOON, photoOnly = MESSAGE_ATTACHMENT_PHOTO_ONLY }) => {
    const [value, setValue] = React.useState("");
    const [attachmentOpen, setAttachmentOpen] = React.useState(false);
    const [items, setItems] = React.useState([]);
    const [attachmentError, setAttachmentError] = React.useState("");
    const submitLockRef = React.useRef(false);
    const localIdRef = React.useRef(0);
    const itemsRef = React.useRef(items);
    const fileInputRef = React.useRef(null);

    const focusInput = React.useCallback(() => {
        const focus = () => inputRef?.current?.focus();
        if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
            window.requestAnimationFrame(focus);
        } else {
            focus();
        }
        if (typeof window !== "undefined") {
            window.setTimeout?.(focus, 0);
        }
    }, [inputRef]);

    itemsRef.current = items;

    // 첨부 제한 안내는 빨간 오류가 아니라 중립 안내다. 2초 뒤 자동으로 사라지게 한다.
    React.useEffect(() => {
        if (!attachmentError) return undefined;
        const timer = window.setTimeout(() => setAttachmentError(""), 2000);
        return () => window.clearTimeout(timer);
    }, [attachmentError]);

    const updateItem = (localId, patch) => {
        setItems((current) => current.map((item) => (item.localId === localId ? { ...item, ...patch } : item)));
    };

    const removeItem = (localId) => {
        setItems((current) => {
            const target = current.find((item) => item.localId === localId);
            if (target) {
                target.controller?.abort();
                if (target.previewUrl) URL.revokeObjectURL(target.previewUrl);
            }
            return current.filter((item) => item.localId !== localId);
        });
    };

    const startUpload = (item) => {
        if (typeof onUploadAttachment !== "function") {
            updateItem(item.localId, { status: "error", error: "업로드를 사용할 수 없어요" });
            return;
        }
        onUploadAttachment(item.file, {
            onProgress: (percent) => updateItem(item.localId, { progress: percent }),
            signal: item.controller?.signal,
        })
            .then((result) => {
                const attachmentId = result?.attachmentId ?? result?.id;
                if (!attachmentId) throw new Error("no_attachment_id");
                updateItem(item.localId, { status: "done", progress: 100, attachmentId });
            })
            .catch((error) => {
                if (error?.name === "CanceledError" || error?.name === "AbortError") return;
                updateItem(item.localId, { status: "error", error: "업로드에 실패했어요" });
            });
    };

    const addFiles = (fileList) => {
        const files = Array.from(fileList || []);
        if (!files.length) return;
        const availableSlots = MESSAGE_ATTACHMENT_LIMITS.maxCount - itemsRef.current.length;
        if (availableSlots <= 0 || files.length > availableSlots) {
            setAttachmentError(`첨부파일은 최대 ${MESSAGE_ATTACHMENT_LIMITS.maxCount}개까지 선택할 수 있어요.`);
            return;
        }

        const pendingFiles = files.map((file) => ({ file, kind: attachmentKindForPolicy(file, photoOnly) }));
        // 사진-only: picker accept 우회(모든 파일)·향후 다른 진입 경로까지 여기서 막는다.
        // 크기 제한과 같은 방식으로 배치 전체를 거부한다.
        if (photoOnly && pendingFiles.some(({ kind }) => kind !== "image")) {
            setAttachmentError(PHOTO_ONLY_NOTICE);
            return;
        }
        const invalid = pendingFiles.find(({ file, kind }) => validateAttachmentFile(file, kind));
        if (invalid) {
            setAttachmentError(validateAttachmentFile(invalid.file, invalid.kind));
            return;
        }

        const nextItems = pendingFiles.map(({ file, kind }) => {
            localIdRef.current += 1;
            const previewUrl = kind === "image" || kind === "video" ? URL.createObjectURL(file) : "";
            const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
            return {
                localId: localIdRef.current,
                file,
                kind,
                previewUrl,
                controller,
                status: "uploading",
                progress: 0,
                attachmentId: null,
                error: "",
            };
        });

        setAttachmentError("");
        setItems((current) => [...current, ...nextItems]);
        nextItems.forEach(startUpload);
    };

    const handleActionClick = (action) => {
        // hotfix-20260628: 첨부 기능 운영 임시 차단.
        // 차단 중(comingSoon=true)이면 파일 선택창을 열지 않고 "서비스 준비중입니다." 안내만 노출한다.
        if (comingSoon) {
            setAttachmentError("서비스 준비중입니다.");
            return;
        }
        // 차단 해제(false) 시 기존 첨부 경로 복구: 선택 콜백 → accept 설정 → file picker 오픈.
        onAttachmentSelect?.(action.key);
        if (fileInputRef.current) {
            fileInputRef.current.accept = action.accept;
            fileInputRef.current.click();
        }
    };

    const handleFileInputChange = (event) => {
        // hotfix-20260628: 심층 방어 — 운영 차단 상태(comingSoon=true)면 업로드 파이프라인에 진입하지 않는다.
        // (UI 진입점 handleActionClick과 더불어 file input onChange 경로도 이중 차단)
        if (comingSoon) {
            event.target.value = "";
            setAttachmentError("서비스 준비중입니다.");
            return;
        }
        addFiles(event.target.files);
        event.target.value = "";
        setAttachmentOpen(false);
    };

    const resetItems = () => {
        itemsRef.current.forEach((item) => {
            if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
        });
        setItems([]);
    };

    React.useEffect(() => () => {
        itemsRef.current.forEach((item) => {
            item.controller?.abort();
            if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
        });
    }, []);

    // 사진-only 면 tray 에 사진 액션만 남긴다(눌러도 막히는 버튼을 두지 않는다).
    const visibleActions = photoOnly ? attachmentActions.filter((action) => action.kind === "image") : attachmentActions;

    const uploading = items.some((item) => item.status === "uploading");
    const hasUploadError = items.some((item) => item.status === "error");
    const readyAttachments = items.filter(
        (item) => item.status === "done" && item.attachmentId && (!photoOnly || item.kind === "image"),
    );
    const hasSendableText = Boolean(value.trim());
    const canSend = !disabled && !sending && !uploading && !hasUploadError && (hasSendableText || readyAttachments.length > 0);

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!canSend || submitLockRef.current) return;
        submitLockRef.current = true;
        const body = value.trim();
        const attachmentIds = readyAttachments.map((item) => item.attachmentId);
        try {
            const sent = await onSend?.({ body, attachmentIds });
            if (sent === false) return;
            setValue("");
            setAttachmentError("");
            resetItems();
            setAttachmentOpen(false);
            focusInput();
        } finally {
            submitLockRef.current = false;
        }
    };

    return (
        <div className={`message-input-shell${attachmentOpen ? " message-input-shell--attachments" : ""}`}>
            <input
                ref={fileInputRef}
                className="sr-only"
                type="file"
                multiple
                data-testid="message-attachment-file-input"
                tabIndex={-1}
                aria-hidden="true"
                onChange={handleFileInputChange}
            />

            {attachmentError && !attachmentOpen && (
                <p className="message-attachment-notice" role="status">
                    {attachmentError}
                </p>
            )}

            {items.length > 0 && (
                <ul className="message-attachment-preview" aria-label="첨부 미리보기">
                    {items.map((item) => (
                        <li
                            key={item.localId}
                            className={`message-attachment-preview__item is-${item.status} is-${item.kind}`}
                        >
                            {item.kind === "image" && item.previewUrl && (
                                <img src={item.previewUrl} alt={`${item.file.name} 미리보기`} />
                            )}
                            {item.kind === "video" && item.previewUrl && (
                                <video src={item.previewUrl} muted aria-label={`${item.file.name} 미리보기`} />
                            )}
                            {item.kind === "file" && (
                                <span className="message-attachment-preview__file" aria-hidden="true">📎</span>
                            )}

                            <span className="message-attachment-preview__name">{item.file.name}</span>

                            {item.status === "uploading" && (
                                <span
                                    className="message-attachment-preview__progress"
                                    role="progressbar"
                                    aria-valuemin={0}
                                    aria-valuemax={100}
                                    aria-valuenow={item.progress}
                                    aria-label={`${item.file.name} 업로드 ${item.progress}%`}
                                >
                                    <i style={{ width: `${item.progress}%` }} />
                                </span>
                            )}
                            {item.status === "error" && (
                                <span className="message-attachment-preview__error" role="alert">
                                    {item.error}
                                </span>
                            )}

                            <button
                                type="button"
                                className="message-attachment-preview__remove"
                                aria-label={`${item.file.name} 첨부 삭제`}
                                onClick={() => removeItem(item.localId)}
                            >
                                ×
                            </button>
                        </li>
                    ))}
                </ul>
            )}

            <form className="message-input" data-testid="message-input-form" onSubmit={handleSubmit}>
                <button
                    type="button"
                    aria-label={attachmentOpen ? "첨부 메뉴 닫기" : "첨부 메뉴 열기"}
                    aria-expanded={attachmentOpen}
                    disabled={disabled}
                    onClick={() => setAttachmentOpen((open) => !open)}
                >
                    {attachmentOpen ? "×" : "＋"}
                </button>
                <label className="sr-only" htmlFor="message-input">메시지 입력</label>
                <input
                    ref={inputRef}
                    id="message-input"
                    type="text"
                    placeholder="메시지 보내기"
                    value={value}
                    onFocus={(event) => {
                        setAttachmentOpen(false);
                        // 입력창 focus(=키보드 오픈)를 부모로 올려, 최신 메시지가 입력창 위에
                        // 보이도록 하단 강제 스크롤을 트리거한다.
                        onFocus?.(event);
                    }}
                    onBlur={(event) => onBlur?.(event)}
                    onChange={(event) => setValue(event.target.value)}
                    disabled={disabled}
                />
                <button
                    type="submit"
                    aria-label="메시지 보내기"
                    disabled={!canSend}
                    onPointerDown={(event) => event.preventDefault()}
                    onMouseDown={(event) => event.preventDefault()}
                >
                    ↑
                </button>
            </form>

            {attachmentOpen && (
                <div className="message-attachment-tray" aria-label="첨부 메뉴" role="region">
                    {attachmentError && (
                        <p className="message-attachment-notice" role="status">
                            {attachmentError}
                        </p>
                    )}
                    <p>키보드 영역에서 첨부를 선택해요</p>
                    <div className="message-attachment-tray__grid">
                        {visibleActions.map((action) => (
                            <button
                                key={action.key}
                                type="button"
                                aria-label={action.label}
                                onClick={() => handleActionClick(action)}
                            >
                                <span aria-hidden="true">{action.icon}</span>
                                <strong>{action.label.replace(" 첨부", "")}</strong>
                                <em>{action.hint}</em>
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export const MessageListEmptyState = () => {
    const navigate = useNavigate();

    return (
        <div className="message-list-empty">
            <div className="message-list-empty__icon" aria-hidden="true">↗</div>
            <strong>아직 메시지가 없어요</strong>
            <p>수업, 투어, 커뮤니티 대화가 시작되면 이곳에서 바로 확인할 수 있어요.</p>
            <button type="button" onClick={() => navigate("/community")}>커뮤니티 둘러보기</button>
        </div>
    );
};

export const EmptyConversation = () => (
    <div className="message-empty-room">
        <strong>아직 표시할 대화가 없습니다.</strong>
        <p>메시지를 보내면 이곳에 표시됩니다.</p>
    </div>
);
